import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GitBranch } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/Auth.css';

const Auth = () => {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInWithGoogle();
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();

    // Validation
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    if (isSignUp && !inviteCode) {
      setError('Invite code is required to sign up');
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (isSignUp && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage('');

      if (isSignUp) {
        await signUpWithEmail(email, password, inviteCode);
        setSuccessMessage('Check your email to confirm your account!');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setInviteCode('');
      } else {
        await signInWithEmail(email, password);
      }
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();

    if (!waitlistEmail.trim()) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(waitlistEmail)) {
      return;
    }

    setWaitlistSubmitting(true);

    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([
          { email: waitlistEmail.toLowerCase() }
        ]);

      if (error) {
        console.error('Error adding to waitlist:', error);
      } else {
        setWaitlistSuccess(true);
        setWaitlistEmail('');
      }
    } catch (err) {
      console.error('Error in waitlist submission:', err);
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <GitBranch size={48} strokeWidth={1.5} className="branch-logo" />
          <h1>Aritra</h1>
          <p>Sign in to continue your conversation</p>
        </div>

        {error && (
          <div className="auth-error">
            <p>{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="auth-success">
            <p>{successMessage}</p>
          </div>
        )}

        {/* OAuth Buttons */}
        <div className="auth-buttons">
          <button
            className="auth-button google"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
              <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
            </svg>
            <span>Continue with Google</span>
          </button>
        </div>

        {/* Divider */}
        <div className="auth-divider">
          <span>OR</span>
        </div>

        {/* Email Form */}
        <form className="auth-form" onSubmit={handleEmailAuth}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="auth-input"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className="auth-input"
          />
          {isSignUp && (
            <>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                className="auth-input"
              />
              <input
                type="text"
                placeholder="Invite Code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                disabled={loading}
                className="auth-input"
                maxLength={8}
              />
            </>
          )}
          <button
            type="submit"
            className="auth-button email-button"
            disabled={loading}
          >
            {isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>

        {/* Waitlist Link */}
        {isSignUp && (
          <div className="auth-waitlist-link">
            <span>Don't have an invite code? </span>
            <button
              type="button"
              className="waitlist-link-button"
              onClick={() => setShowWaitlist(true)}
            >
              Join Waitlist
            </button>
          </div>
        )}

        {/* Toggle Sign In / Sign Up */}
        <div className="auth-toggle">
          <button
            type="button"
            className="toggle-button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
              setSuccessMessage('');
            }}
            disabled={loading}
          >
            {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>

        <div className="auth-footer">
          <p>By signing in, you agree to our Terms of Service and Privacy Policy</p>
        </div>
      </div>

      {/* Waitlist Modal */}
      {showWaitlist && (
        <div className="modal-overlay" onClick={() => setShowWaitlist(false)}>
          <div className="modal waitlist-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Join the Waitlist</h3>

            {!waitlistSuccess ? (
              <>
                <p style={{ color: '#999', fontSize: '14px', marginBottom: '20px' }}>
                  Enter your email and we'll send you an invite code when available.
                </p>

                <form onSubmit={handleWaitlistSubmit}>
                  <input
                    type="email"
                    className="waitlist-input"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    disabled={waitlistSubmitting}
                    autoFocus
                  />

                  <div className="modal-buttons">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => setShowWaitlist(false)}
                      disabled={waitlistSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="create-button"
                      disabled={waitlistSubmitting || !waitlistEmail.trim()}
                    >
                      {waitlistSubmitting ? 'Submitting...' : 'Join Waitlist'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                  <p style={{ color: '#4caf50', fontSize: '16px', marginBottom: '8px' }}>
                    You're on the list!
                  </p>
                  <p style={{ color: '#999', fontSize: '14px' }}>
                    We'll send you an invite code soon.
                  </p>
                </div>

                <div className="modal-buttons">
                  <button
                    className="create-button"
                    onClick={() => {
                      setShowWaitlist(false);
                      setWaitlistSuccess(false);
                    }}
                    style={{ width: '100%' }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;
